import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { BookOpen, CreditCard, Bluetooth, AlertTriangle } from 'lucide-react';

export const DEFAULT_IDENTITY_LOGBOOK = {
  enabled: false,
  rfid_ibutton_enabled: false,
  ble_enabled: false,
  use_custom_qualification: false,
  trip_qualification: {
    min_ignition_seconds: 60,
    min_distance_meters: 50,
    movement_speed_kmh: 3,
    consecutive_moving_reports: 2,
  },
};

export const normalizeIdentityLogbook = (raw) => ({
  ...DEFAULT_IDENTITY_LOGBOOK,
  ...(raw || {}),
  trip_qualification: {
    ...DEFAULT_IDENTITY_LOGBOOK.trip_qualification,
    ...((raw || {}).trip_qualification || {}),
  },
});

const effectiveModeLabel = (cfg) => {
  if (!cfg.enabled) return 'Off';
  if (cfg.rfid_ibutton_enabled && cfg.ble_enabled) return 'RFID / iButton + BLE Beacons';
  if (cfg.rfid_ibutton_enabled) return 'RFID / iButton';
  if (cfg.ble_enabled) return 'BLE Beacons';
  return 'No source selected';
};

export const IdentityLogbookSettingsSection = ({ value, onChange, ioMappings }) => {
  const cfg = normalizeIdentityLogbook(value);
  const mappings = ioMappings || [];
  const rfidMappings = mappings.filter((m) => m.io_type === 'RFID / iButton' && m.io_id);
  const hasRfidMapping = rfidMappings.length > 0;
  const hasIgnitionMapping = mappings.some((m) => m.io_type === 'Ignition' && m.io_id);

  const set = (patch) => onChange({ ...cfg, ...patch });
  const setTq = (key, val) => set({ trip_qualification: { ...cfg.trip_qualification, [key]: val } });

  return (
    <div className="space-y-3 pt-3 border-t" data-testid="identity-logbook-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> Identity &amp; Logbooks</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Vehicle Driver / Asset Log Book processing for this vehicle. Off by default — enable per vehicle.
          </p>
        </div>
        <Switch
          checked={cfg.enabled}
          onCheckedChange={(v) => set({ enabled: v })}
          data-testid="identity-logbook-master-switch"
        />
      </div>

      {cfg.enabled && (
        <div className="space-y-3 rounded-lg border p-3 bg-muted/20">
          {!hasIgnitionMapping && (
            <div className="rounded-md border border-red-300 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40 px-3 py-2 text-xs text-red-800 dark:text-red-200 flex items-start gap-2" data-testid="identity-logbook-ignition-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Identity Logbooks require an IO Mapping with <strong>IO Type = Ignition</strong>. Add it in the IO Mapping tab before saving.</span>
            </div>
          )}
          {!cfg.rfid_ibutton_enabled && !cfg.ble_enabled && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="identity-logbook-source-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Enable at least one identity source (RFID / iButton or BLE Beacons).</span>
            </div>
          )}

          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Label className="text-sm flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> RFID / iButton</Label>
              {hasRfidMapping ? (
                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="identity-logbook-rfid-mapping-info">
                  Configured via {rfidMappings.map((m) => `AVL IO ${m.io_id}`).join(', ')}
                </p>
              ) : (
                <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5" data-testid="identity-logbook-rfid-mapping-missing">
                  Requires an IO Mapping with IO Type = RFID / iButton
                </p>
              )}
            </div>
            <Switch
              checked={cfg.rfid_ibutton_enabled}
              disabled={!hasRfidMapping}
              onCheckedChange={(v) => set({ rfid_ibutton_enabled: v })}
              data-testid="identity-logbook-rfid-switch"
            />
          </div>

          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Label className="text-sm flex items-center gap-1.5"><Bluetooth className="w-3.5 h-3.5" /> BLE Beacons</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">No IO mapping required — processed from BLE beacon scans.</p>
            </div>
            <Switch
              checked={cfg.ble_enabled}
              onCheckedChange={(v) => set({ ble_enabled: v })}
              data-testid="identity-logbook-ble-switch"
            />
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Effective mode:</span>
            <Badge variant="outline" data-testid="identity-logbook-effective-mode">{effectiveModeLabel(cfg)}</Badge>
          </div>

          <div className="pt-2 border-t space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-sm">Vehicle-specific trip qualification</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {cfg.use_custom_qualification ? 'Using this vehicle\u2019s own thresholds.' : 'Using application defaults (60 s \u00b7 50 m \u00b7 >3 km/h \u00d7 2 reports).'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]" data-testid="identity-logbook-qualification-mode">
                  {cfg.use_custom_qualification ? 'Vehicle override' : 'Inherited defaults'}
                </Badge>
                <Switch
                  checked={cfg.use_custom_qualification}
                  onCheckedChange={(v) => set({ use_custom_qualification: v })}
                  data-testid="identity-logbook-custom-qualification-switch"
                />
              </div>
            </div>
            {cfg.use_custom_qualification && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="identity-logbook-qualification-inputs">
                <div className="space-y-1">
                  <Label className="text-[11px]">Min ignition (s)</Label>
                  <Input type="number" min={1} className="h-8 text-xs" value={cfg.trip_qualification.min_ignition_seconds}
                    onChange={(e) => setTq('min_ignition_seconds', e.target.value === '' ? '' : parseInt(e.target.value))}
                    data-testid="identity-logbook-min-ignition-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Min distance (m)</Label>
                  <Input type="number" min={0} className="h-8 text-xs" value={cfg.trip_qualification.min_distance_meters}
                    onChange={(e) => setTq('min_distance_meters', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    data-testid="identity-logbook-min-distance-input" />
                  <p className="text-[10px] text-muted-foreground">0 disables this rule</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Speed above (km/h)</Label>
                  <Input type="number" min={0} className="h-8 text-xs" value={cfg.trip_qualification.movement_speed_kmh}
                    onChange={(e) => setTq('movement_speed_kmh', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    data-testid="identity-logbook-speed-input" />
                  <p className="text-[10px] text-muted-foreground">0 disables this rule</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Consecutive reports</Label>
                  <Input type="number" min={1} className="h-8 text-xs" value={cfg.trip_qualification.consecutive_moving_reports}
                    disabled={Number(cfg.trip_qualification.movement_speed_kmh) === 0}
                    onChange={(e) => setTq('consecutive_moving_reports', e.target.value === '' ? '' : parseInt(e.target.value))}
                    data-testid="identity-logbook-consecutive-reports-input" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
